import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

type OverflowInfoCellProps = {
  value: string | null | undefined;
  fullValue?: string | null | undefined;
  title?: string;
  placeholder?: string;
  threshold?: number;
  cellClassName?: string;
  textClassName?: string;
  tooltipClassName?: string;
  forceShowInfo?: boolean;
  overflowOnly?: boolean;
  tooltipPlacement?: "left" | "right";
  portalTooltip?: boolean;
};

export const OverflowInfoCell: React.FC<OverflowInfoCellProps> = ({
  value,
  fullValue,
  title,
  placeholder = "-",
  threshold = 36,
  cellClassName = "",
  textClassName = "",
  tooltipClassName = "",
  forceShowInfo = false,
  overflowOnly = false,
  tooltipPlacement = "right",
  portalTooltip = false,
}) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [hasVisualOverflow, setHasVisualOverflow] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const displayText = typeof value === "string" ? value.trim() : "";
  const fullText =
    typeof fullValue === "string" && fullValue.trim()
      ? fullValue.trim()
      : displayText;
  const displayValue = displayText || placeholder;
  const tooltipTitle = title?.trim();

  useEffect(() => {
    const node = textRef.current;
    if (!node) {
      setHasVisualOverflow(false);
      return;
    }

    const updateOverflowState = () => {
      const nextOverflow =
        node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight;
      setHasVisualOverflow(nextOverflow);
    };

    updateOverflowState();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateOverflowState());
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateOverflowState);
    return () => window.removeEventListener("resize", updateOverflowState);
  }, [displayValue]);

  const hasLineBreak =
    displayText.includes("\n") ||
    displayText.includes("\r") ||
    fullText.includes("\n") ||
    fullText.includes("\r");

  const shouldShowInfo = Boolean(fullText) && (
    overflowOnly
      ? forceShowInfo || hasVisualOverflow || hasLineBreak
      : forceShowInfo ||
        hasVisualOverflow ||
        displayText.length > threshold ||
        hasLineBreak ||
        fullText.length > threshold
  );

  const tooltipPositionClass =
    tooltipPlacement === "left"
      ? "absolute right-full top-1/2 z-[70] mr-2 -translate-y-1/2"
      : "absolute left-full top-1/2 z-[70] ml-2 -translate-y-1/2";
  const tooltipArrowClass =
    tooltipPlacement === "left"
      ? "absolute left-full top-1/2 -translate-y-1/2 h-0 w-0 border-y-[6px] border-y-transparent border-l-[6px] border-l-slate-900"
      : "absolute right-full top-1/2 -translate-y-1/2 h-0 w-0 border-y-[6px] border-y-transparent border-r-[6px] border-r-slate-900";
  const portalArrowClass =
    tooltipPlacement === "left"
      ? "absolute left-full top-1/2 -translate-y-1/2 h-0 w-0 border-y-[6px] border-y-transparent border-l-[6px] border-l-slate-900"
      : "absolute right-full top-1/2 -translate-y-1/2 h-0 w-0 border-y-[6px] border-y-transparent border-r-[6px] border-r-slate-900";

  useEffect(() => {
    if (!portalTooltip || !isTooltipVisible) return;

    const updatePortalPosition = () => {
      const triggerNode = triggerRef.current;
      if (!triggerNode) return;

      const rect = triggerNode.getBoundingClientRect();
      setPortalStyle({
        position: "fixed",
        top: rect.top + rect.height / 2,
        left: tooltipPlacement === "left" ? rect.left - 8 : rect.right + 8,
        transform:
          tooltipPlacement === "left"
            ? "translate(-100%, -50%)"
            : "translate(0, -50%)",
        zIndex: 200,
      });
    };

    updatePortalPosition();
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);

    return () => {
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [isTooltipVisible, portalTooltip, tooltipPlacement]);

  return (
    <div className={`flex items-center gap-2 min-w-0 ${cellClassName}`.trim()}>
      <span
        ref={textRef}
        className={`block truncate min-w-0 flex-1 ${textClassName}`.trim()}
        title={!shouldShowInfo ? displayValue : undefined}
      >
        {displayValue}
      </span>
      {shouldShowInfo && (
        <div
          ref={triggerRef}
          className={`relative flex-shrink-0 ${portalTooltip ? "" : "group/info"}`.trim()}
          onMouseEnter={portalTooltip ? () => setIsTooltipVisible(true) : undefined}
          onMouseLeave={portalTooltip ? () => setIsTooltipVisible(false) : undefined}
        >
          <InformationCircleIcon className="w-4 h-4 text-sky-500 cursor-help" />
          {portalTooltip
            ? isTooltipVisible &&
              typeof document !== "undefined" &&
              createPortal(
                <div
                  style={portalStyle}
                  className={`pointer-events-none w-72 max-w-[28rem] rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-2xl ${tooltipClassName}`.trim()}
                >
                  {tooltipTitle && (
                    <div className="mb-1 font-semibold text-slate-100">
                      {tooltipTitle}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{fullText}</div>
                  <div className={portalArrowClass} />
                </div>,
                document.body
              )
            : (
                <div
                  className={`pointer-events-none invisible opacity-0 group-hover/info:visible group-hover/info:opacity-100 transition-opacity ${tooltipPositionClass} w-72 max-w-[28rem] rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-2xl ${tooltipClassName}`.trim()}
                >
                  {tooltipTitle && (
                    <div className="mb-1 font-semibold text-slate-100">
                      {tooltipTitle}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{fullText}</div>
                  <div className={tooltipArrowClass} />
                </div>
              )}
        </div>
      )}
    </div>
  );
};
